/**
 * Resolves the CUSTOMER a supplier-portal user is reporting against.
 *
 * The analytics endpoints are keyed on the customer, not the supplier: a
 * supplier user's own company is the wrong axis, and
 * `analytics/scope_resolver.py` returns 403 ("customer_id is required for
 * supplier users") when the parameter is missing. A supplier can trade with
 * several customers, so the caller also needs to let them switch.
 *
 * Customer-portal and Helix users get `customers: []` and `customerId: null`
 * — they scope through the site switcher and must not send `customer_id`.
 *
 * The tri-state on `customerId` is load-bearing:
 *   `undefined` — still resolving; hold the fetch, keep showing the spinner
 *   `null`      — resolved to nothing; the call can never be authorised, so
 *                 render an empty state instead of spinning forever
 *   `number`    — send it
 *
 * Without the `undefined`/`null` split, a supplier with no trading
 * relationship would sit on a spinner with no request in flight to end it.
 *
 * @param isSupplier - true when the user is on the supplier portal
 * @returns The resolved customer id, the full list for a picker, and a setter
 *
 * @example
 * const { customerId, customers, setCustomerId } = useSupplierCustomer(portalIsSupplier);
 * if (portalIsSupplier && customerId === undefined) return;   // still resolving
 * if (portalIsSupplier && customerId === null) return;        // nothing to show
 */
import { useState, useEffect, useCallback } from 'react'
import { coreAPI } from '../../lib/api'

export interface SupplierCustomerOption {
  id: number
  name: string
}

export interface UseSupplierCustomerResult {
  /** `undefined` = resolving, `null` = none available, number = use it. */
  customerId: number | null | undefined
  customers: SupplierCustomerOption[]
  setCustomerId: (id: number) => void
  loading: boolean
}

export function useSupplierCustomer(isSupplier: boolean): UseSupplierCustomerResult {
  const [customerId, setCustomerId] = useState<number | null | undefined>(undefined)
  const [customers, setCustomers] = useState<SupplierCustomerOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Not a supplier: settle immediately on `null` so the consumer's fetch
    // guard falls through rather than waiting on a resolution that never runs.
    if (!isSupplier) {
      setCustomerId(null)
      setCustomers([])
      return
    }
    let cancelled = false
    setCustomerId(undefined)
    setLoading(true)
    ;(async () => {
      try {
        // ?context=customer_filter flips the accessible-companies list from
        // the supplier's own hierarchy to the customers it serves — the same
        // source the Dashboard customer dropdown, ScheduledTab and
        // POLineDashboardTab already use.
        const resp = await coreAPI.getAccessibleCompanies({ context: 'customer_filter' })
        if (cancelled) return
        const raw = resp.data as unknown
        const results: Array<{ id: number; name?: string }> =
          Array.isArray(raw)
            ? (raw as Array<{ id: number; name?: string }>)
            : Array.isArray((raw as { results?: unknown }).results)
              ? (raw as { results: Array<{ id: number; name?: string }> }).results
              : []
        const list = results
          .map((c) => ({ id: c.id, name: c.name || `Customer ${c.id}` }))
          .sort((a, b) => a.name.localeCompare(b.name))
        setCustomers(list)
        // Always settle on a definite value so the consumer's fetch can proceed.
        setCustomerId(list.length > 0 ? list[0]!.id : null)
      } catch {
        // A failed lookup is indistinguishable from "no customers" as far as
        // the endpoint is concerned — either way the call cannot be authorised.
        if (!cancelled) {
          setCustomers([])
          setCustomerId(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isSupplier])

  const select = useCallback((id: number) => setCustomerId(id), [])

  return { customerId, customers, setCustomerId: select, loading }
}
